import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Cuboid } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'components/ui/form';
import { Input } from 'components/ui/input';
import { useAppSelector } from 'features/hooks/useRedux';
import { selectCreateOrder } from 'features/store/slices/createOrder';
import { useCreateSegment } from 'api/segments';
import { QUERY_KEY_GET_SEGMENTS } from 'api/keys';

const schema = z.object({
  title: z.string().min(1, 'Поле обязательно для заполнения'),
});

type FormValues = z.infer<typeof schema>;

export const CreateSegment = () => {
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);

  const { order } = useAppSelector(selectCreateOrder);

  const { mutateAsync: createSegment, isPending: isPendingCreateSegment } = useCreateSegment();

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await createSegment({
        channels: order.channels.map((el) => el.id),
        title: data.title,
      });
      await queryClient.refetchQueries({
        queryKey: [QUERY_KEY_GET_SEGMENTS],
      });
      setShowModal(false);
      form.reset();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <div>
        <Button size={'sm'} onClick={toggleModal}>
          Создать сегмент
        </Button>
      </div>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Создание сегмента</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Название сегмента</FormLabel>
                    <FormControl>
                      <Input type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <p>Каналов в сегменте: {order.channels.length}</p>

              <DialogFooter>
                <Button
                  className="w-full text-white hover:opacity-90"
                  type="submit"
                  disabled={isPendingCreateSegment || !form.formState.isValid}
                >
                  Создать
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};
